import { HttpProvider } from "../http/http.provider";
import { ServiceEndpointConstants } from "../ServiceUtil";
import { Observable } from "rxjs";
import { share, map } from "rxjs/operators";
import {
    INewEmployee,
    IEmployee,
    IEmployeeAddress,
    IEmployeeInsurance,
    IEmpPayroll,
    INewEmployeeAddress,
    INewEmployeeDocument,
    IEmployeeContractDocument,
    INewEmploymentHistory,
    IEmploymentHistory,
    IEmployeeResponse,
    INewPayrollRate,
    IPayrollRate,
    INewEmployeeInsurance,
    IOnboardEmployee,
    INewEmployeeEmploymentStatus,
    IEmployeeEmploymentStatus,
    INewEmployeeAccount,
    IEmployeeAccount,
    INewEmployeeBulk,
    IEmployeeEmergencyContact,
    INewEmployeeEmergencyContact,
    IEmployeeEducationHistory,
    INewEmployeeEducationHistory,
    IOrgTreeNode,
    IManagerEmployee,
    IJoiningFormInvite,
    INewJoiningFormInvite,
    IJoiningFormInviteResponse,
    INewJoiningFormSubmission,
    IJoiningFormSubmitResponse,
    IJoiningFormDocumentResponse,
} from "./employee.interface";
import { IHttpRequestConfig } from "../http/types/http.types";
import { createFormData } from "../util/utilities";
import { ProjectDelegate } from "../ProjectService/project.delegate";
import { catchError } from "rxjs/operators";
import { of } from "rxjs";

/**
 * 
 */

export class EmployeeDelegate {
    constructor(private http: HttpProvider) {
    } 

public getAllEmployeesByOrgId(orgId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET, null, config).pipe(
            map((response: any) => {
                return {
                    employees: this.normalizeEmployees(response.data.employees),
                    success: response.data.success
                };
            }),
            share());
    }

    public getAllOnboardEmployeesByOrgId(orgId: string, userId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                orgId: orgId,
                userId: userId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_ONBOARD_EMPLOYEES, null, config).pipe(
            map((response: any) => {
                return {
                    onboardEmployees: this.normalizeOnboardEmployees(response.data.onboardEmployees),
                    success: response.data.success
                };
            }),
            share());
    }

    public getOnboardEmployeesByEmployeeId(onboardEmployeeId: string, userId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                onboardEmployeeId: onboardEmployeeId,
                userId: userId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_ONBOARD_EMPLOYEE_ID, null, config).pipe(
            map((response: any) => {
                return {
                    onboardEmployee: this.normalizeOnboardEmployee(response.data.onboardingEmployee),
                    onboardEmployeeId: onboardEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public getEmployeeEmployeeId(employeeId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                EmployeeId: employeeId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_EMPLOYEE_BY_EMPLOYEE_ID, null, config).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public getTimeSheetSpecsEmployeeId(employeeId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                EmployeeId: employeeId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_TIMESHEET_SPEC_BY_EMPLOYEE_ID, null, config).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewEmployeeBulk(newBenchLead: INewEmployeeBulk): Observable<IEmployeeResponse> {
        const formdata = createFormData(newBenchLead);

        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_BULK, formdata).pipe(
            map((response: any) => {
                return {
                    employees: this.normalizeEmployees(response.data.employees),
                    resourceId: response.data.resourceId,
                    message: response.data.message,
                    success: response.data.success
                };
            }),
            share());
    }


    public updateOnboardEmployee(employee: IOnboardEmployee): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_ONBOARD_EMPLOYEE, employee).pipe(
            map((response: any) => {
                return {
                    onboardEmployee: this.normalizeOnboardEmployee(response.data.onboardEmployee),
                    onboardEmployeeId: employee.onboardEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewEmployee(newEmployee: INewEmployee): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE, newEmployee).pipe(
            map((response: any) => {
                return {
                    employees: this.normalizeEmployees(response.data.employees),
                    employeeId: response.data.newEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewEmployeeInsurance(newEmployeeInsurance: INewEmployeeInsurance): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_INSURANCE, newEmployeeInsurance).pipe(
            map((response: any) => {
                return {
                    employeeInsuranceId: response.data.employeeInsuranceId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployeeInsurance(newEmployeeInsurance: IEmployeeInsurance): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_INSURANCE, newEmployeeInsurance).pipe(
            map((response: any) => {
                return {
                    employeeInsuranceId: response.data.employeeInsuranceId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployee(employee: IEmployee): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE, employee).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: employee.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmployeeAddress(newemployeeAddress: INewEmployeeAddress): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.SAVE_EMPLOYEE_ADDRESS, newemployeeAddress).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeAddressId: response.data.newEmployeeAddressId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmployeeAccount(newemployeeAddress: INewEmployeeAccount): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_ACCOUNT, newemployeeAddress).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeAccountId: response.data.newEmployeeAccountId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployeeAccount(employeeAddress: IEmployeeAccount): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_ACCOUNT, employeeAddress).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeAccountId: employeeAddress.employeeAccountId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmployeeEmploymentStatus(newEmployeeEmploymentStatus: INewEmployeeEmploymentStatus): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.SAVE_EMPLOYEE_EMPLOYMENT_STATUS, newEmployeeEmploymentStatus).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeEmploymentStatusId: response.data.employeeEmploymentStatusId,
                    success: response.data.success
                };
            }),
            share());
    }
    public updateEmployeeEmploymentStatus(employeeEmploymentStatus: IEmployeeEmploymentStatus): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_EMPLOYMENT_STATUS, employeeEmploymentStatus).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeEmploymentStatusId: employeeEmploymentStatus.employeeEmploymentStatusId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmployeePayRate(newPayRate: INewPayrollRate): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.SAVE_EMPLOYEE_PAYRATE, newPayRate).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeePayRateId: response.data.employeePayRateId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployeeAddress(employeeAddress: IEmployeeAddress): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_ADDRESS, employeeAddress).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeAddressId: employeeAddress.employeeAddressId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployeePayRate(employeeAddress: IPayrollRate): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_PAYRATE, employeeAddress).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeePayRateId: employeeAddress.employeePayRateId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployeeDocuments(documents: INewEmployeeDocument): Observable<IEmployeeResponse> {
        const formdata = createFormData(documents);

        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_DOCUMENT, formdata).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: documents.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmployeeDocuments(documents: INewEmployeeDocument): Observable<IEmployeeResponse> {
        const formdata = createFormData(documents);

        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_DOCUMENT, formdata).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: documents.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmployeeHistory(history: INewEmploymentHistory): Observable<IEmployeeResponse> {
        const formdata = createFormData(history);

        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_HISTORY, formdata).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: history.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmployeeHistory(history: IEmploymentHistory): Observable<IEmployeeResponse> {
        const formdata = createFormData(history);

        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_HISTORY, formdata).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeEmploymentId: history.employeeEmploymentId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEmergencyContact(contact: INewEmployeeEmergencyContact): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMERGENCY_CONTACT, contact).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: contact.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEmergencyContact(contact: IEmployeeEmergencyContact): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMERGENCY_CONTACT, contact).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: contact.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createEducationHistory(education: INewEmployeeEducationHistory): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EDUCATION_HISTORY, education).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: education.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateEducationHistory(education: IEmployeeEducationHistory): Observable<IEmployeeResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EDUCATION_HISTORY, education).pipe(
            map((response: any) => {
                return {
                    employee: this.normalizeEmployee(response.data.employee),
                    employeeId: education.employeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public getOrgReportingTree(orgId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: { orgId: orgId }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_ORG_REPORTING_TREE, null, config).pipe(
            map((response: any) => {
                return {
                    tree: EmployeeDelegate.normalizeTreeNodes(response.data.tree || []),
                    success: response.data.success
                };
            }),
            share());
    }

    public getSubordinateTree(managerEmployeeId: string): Observable<IEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: { managerEmployeeId: managerEmployeeId }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_SUBORDINATE_TREE, null, config).pipe(
            map((response: any) => {
                return {
                    tree: EmployeeDelegate.normalizeTreeNodes(response.data.tree || []),
                    success: response.data.success
                };
            }),
            share());
    }

    private normalizeEmployee(employee: any): IEmployee {
        employee.createEmployeeLogin = !!employee.userId;
        employee.employeeFullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();

        // Reporting structure fields
        employee.reportingManagerName = employee.reportingManagerName
            || (employee.reportingManager
                ? `${employee.reportingManager.firstName || ''} ${employee.reportingManager.lastName || ''}`.trim()
                : '');
        employee.orgLevelName = employee.orgLevelName ?? '';
        employee.orgLevelRank = employee.orgLevelRank ?? 0;
        employee.isManager = !!employee.isManager;
        employee.orgLevelId = employee.orgLevelId ?? null;
        employee.jobTitle = employee.jobTitle ?? employee.designation ?? '';

        employee.employeeInsurance = employee.employeeInsurance;
        employee.employeePayroll = this.normalizePayroll(employee.employeePayrolls);
        employee.employeeDocuments = this.normalizeDocuments(employee.employeeDocuments);
        employee.employeeProjectCodes = ProjectDelegate.normalizeProjectCodes(employee.projectCodeEmployees);
        employee.projectCodeAsignedEmployee = ProjectDelegate.normalizeProjectCodes(employee.projectCodeAsignedEmployees);

        employee.employeeEmployment = this.normalizeEmployementHistory(employee.employeeEmployments);
        employee.employeeDocumentArchive = this.normalizeDocuments(employee.employeeDocumentArchives);
        employee.employeeEmploymentStatus = employee.employeeEmploymentStatuses;

        // Emergency contacts: primary contact first
        const rawContacts: IEmployeeEmergencyContact[] = employee.employeeEmergencyContacts
            ? employee.employeeEmergencyContacts
            : [];
        employee.employeeEmergencyContacts = rawContacts.slice().sort((a, b) => {
            if (a.isPrimary === b.isPrimary) { return 0; }
            return a.isPrimary ? -1 : 1;
        });

        // Education history: highest qualification first, then by endYear descending (null/0 treated as still enrolled → last)
        const rawEducation: IEmployeeEducationHistory[] = employee.employeeEducationHistories
            ? employee.employeeEducationHistories
            : [];
        employee.employeeEducationHistories = rawEducation.slice().sort((a, b) => {
            if (a.isHighestQualification !== b.isHighestQualification) {
                return a.isHighestQualification ? -1 : 1;
            }
            const aYear = a.endYear || 0;
            const bYear = b.endYear || 0;
            return bYear - aYear;
        });

        // Employment history sorted by contractStartDate desc
        employee.employeeEmployment = (employee.employeeEmployment as IEmploymentHistory[]).slice().sort((a, b) => {
            const aDate = a.contractStartDate ? new Date(a.contractStartDate).getTime() : 0;
            const bDate = b.contractStartDate ? new Date(b.contractStartDate).getTime() : 0;
            return bDate - aDate;
        });

        delete employee.employeeDocument;

        return employee;
    }
    private normalizeEmployees(employees: any): IEmployee[] {
        return employees.map(employee => {
            return this.normalizeEmployee(employee);
        });
    }

    private normalizeOnboardEmployee(employee: any): IOnboardEmployee {
        return employee;
    }

    private normalizeOnboardEmployees(employees: any): IOnboardEmployee[] {
        return employees.map(employee => {
            return employee;
        });
    }


    private normalizeInsurance(insurances: any): IEmployeeInsurance[] {
        return insurances.map(insurance => {
            return {
                healthInsuranceDetailId: insurance.healthInsuranceDetailId,
                employeeInsuranceId: insurance.employeeInsuanceId,
                dentalInsuranceDetailId: insurance.dentalInsuranceDetailId,
                visionInsuranceDetailId: insurance.visionInsuranceDetailId,
                startDate: insurance.startDate,
                endDate: insurance.endDate,
                comments: insurance.comments,
                active: insurance.active
            } as IEmployeeInsurance;
        });
    }

    private normalizePayroll(payroll: any): IEmpPayroll[] {
        return payroll.map(payroll => {
            return {
                employeePayrollId: payroll.employeePayrollId,
                fileLocationPath: payroll.fileLocationPath,
                payrollDateId: payroll.employeePayrollRecordId,
                fileName: payroll.fileName,
                employeePayrollDate: payroll.employeePayrollRecord.payDateToString,
                hideCheck: payroll.hideCheck
            } as IEmpPayroll;
        });
    }

    private normalizeDocuments(documents: any): IEmployeeContractDocument[] {
        return documents.map(document => {
            return {
                employeeDocumentId: document.employeeDocumentId,
                employeeDocumentType: document.employeeDocumentType,
                employeeDocumentTypeValue: document.employeeDocumentType ? document.employeeDocumentType.configurationValue : "",
                fileLocationPath: document.fileLocationPath,
                fileName: document.fileName,
                fileExpiryDateToString: document.fileExpiryDateToString,
                isVisibleToEmployee: document.isVisibleToEmployee
            } as IEmployeeContractDocument;
        });
    }

    private normalizeEmployementHistory(employments: any): IEmploymentHistory[] {
        return employments.map(employment => {
            // employment.contractStartDate = employment.contractStartDateToString;
            // employment.contractEndDate = employment.contractEndDateToString;
            // employment.hireDate = employment.hireDateToString;
            // employment.terminationDate = employment.terminationDateToString;
            // delete employment.contractStartDateToString;
            // delete employment.contractEndDateToString;
            // delete employment.hireDateToString;
            // delete employment.terminationDateToString;
            employment.employmentDocuments = employment.employmentDocuments
                ? this.normalizeDocuments(employment.employmentDocuments)
                : [];
            return employment;
        });
    }

   
    



    public static normalizeTreeNodes(nodes: any[]): IOrgTreeNode[] {
        return nodes.map(node => EmployeeDelegate.normalizeTreeNode(node));
    }

    private static normalizeTreeNode(node: any): IOrgTreeNode {
        return {
            employeeId: node.employeeId,
            firstName: node.firstName || '',
            lastName: node.lastName || '',
            employeeFullName: `${node.firstName || ''} ${node.lastName || ''}`.trim(),
            jobTitle: node.jobTitle || node.designation || '',
            emailAddress: node.emailAddress || '',
            empLocation: node.empLocation || '',
            active: !!node.active,
            isManager: !!node.isManager,
            orgLevelId: node.orgLevelId ?? null,
            orgLevelName: node.orgLevelName || '',
            orgLevelRank: node.orgLevelRank ?? 0,
            departmentId: node.departmentId ?? null,
            departmentName: node.departmentName || '',
            positionId: node.positionId ?? null,
            positionName: node.positionName || '',
            reportingManagerId: node.reportingManagerId || null,
            reportingManagerName: node.reportingManagerName || '',
            hierarchyLevel: node.hierarchyLevel ?? 0,
            hierarchyPath: node.hierarchyPath || '',
            directReports: node.directReports?.length
                ? EmployeeDelegate.normalizeTreeNodes(node.directReports)
                : []
        };
    }

    public static flattenTree(nodes: IOrgTreeNode[]): IOrgTreeNode[] {
        const result: IOrgTreeNode[] = [];
        const walk = (arr: IOrgTreeNode[]) => {
            for (const node of arr) {
                result.push({ ...node, directReports: [] });
                if (node.directReports?.length) { walk(node.directReports); }
            }
        };
        walk(nodes);
        return result;
    }

    public static buildManagerList(nodes: IOrgTreeNode[]): IManagerEmployee[] {
        return EmployeeDelegate.flattenTree(nodes)
            .filter(n => n.isManager)
            .sort((a, b) => {
                const rankDiff = (b.orgLevelRank ?? 0) - (a.orgLevelRank ?? 0);
                if (rankDiff !== 0) { return rankDiff; }
                return (a.lastName || '').localeCompare(b.lastName || '');
            })
            .map(n => ({
                employeeId: n.employeeId,
                employeeFullName: `${n.employeeFullName} — ${n.jobTitle || n.orgLevelName || ''}`.trimEnd().replace(/ — $/, ''),
                jobTitle: n.jobTitle,
                orgLevelName: n.orgLevelName,
                orgLevelRank: n.orgLevelRank
            }));
    }

    public sendJoiningFormInvite(invite: INewJoiningFormInvite): Observable<IJoiningFormInviteResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.SEND_JOINING_FORM_INVITE, invite).pipe(
            map((response: any) => {
                return {
                    invite: response.data.invite,
                    success: response.data.success
                };
            }),
            share());
    }

    public getAllJoiningFormInvites(orgId: string): Observable<IJoiningFormInviteResponse> {
        const url = ServiceEndpointConstants.endpoints.EMPLOYEES.GET_ALL_JOINING_FORM_INVITES + orgId;
        return this.http.get(url).pipe(
            map((response: any) => {
                return {
                    invites: response.data.invites || [],
                    success: response.data.success
                };
            }),
            share());
    }

    public validateJoiningFormToken(token: string): Observable<IJoiningFormInviteResponse> {
        const config: IHttpRequestConfig = {
            headers: { Authorization: `InviteToken ${token}` },
            withCredentials: false
        };
        return this.http.get(ServiceEndpointConstants.endpoints.JOINING_FORM.VALIDATE, null, config).pipe(
            map((response: any) => {
                return {
                    invite: response.data.invite,
                    success: response.data.success,
                    errorString: response.data.errorString
                };
            }),
            share());
    }

    public submitJoiningForm(token: string, submission: INewJoiningFormSubmission): Observable<IJoiningFormSubmitResponse> {
        const config: IHttpRequestConfig = {
            headers: { Authorization: `InviteToken ${token}` },
            withCredentials: false
        };
        const formdata = createFormData(submission);

        return this.http.post(ServiceEndpointConstants.endpoints.JOINING_FORM.SUBMIT, formdata, config).pipe(
            map((response: any) => {
                return {
                    submission: response.data.submission,
                    success: response.data.success,
                    errorString: response.data.errorString
                };
            }),
            share());
    }

    public uploadJoiningFormDocuments(token: string, files: File[], documentTypes: string[]): Observable<IJoiningFormDocumentResponse> {
        const formdata = new FormData();
        files.forEach(f => formdata.append('Files', f, f.name));
        documentTypes.forEach(t => formdata.append('DocumentTypes', t));

        const config: IHttpRequestConfig = {
            headers: { Authorization: `InviteToken ${token}` },
            withCredentials: false
        };
        return this.http.post(ServiceEndpointConstants.endpoints.JOINING_FORM.UPLOAD_DOCUMENTS, formdata, config).pipe(
            map((response: any) => {
                return {
                    documents: response.data.documents || [],
                    success: response.data.success,
                    errorString: response.data.errorString
                };
            }),
            share());
    }

}